import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { X, Home, MessageCircle, UserPlus } from "lucide-react";
import moment from "moment";
import { useUser } from "@/context/UserContext";
import {
    InAppNotification,
    getInAppNotifications,
    markInAppNotificationAsRead,
    markAllInAppNotificationsAsRead,
} from "@/services/NotificationService";
import { logger } from "@/services/LoggerService";
import { navigateToPuzzleScoreboardFromNotification } from "@/helpers/puzzle-notification-navigation";
import { Heart } from "lucide-react";
import useMixpanelEvent, { type MixpanelNotificationType } from "@/hooks/useMixpanelEvent";

export interface AppNotification {
    id: string;
    type: "agent_puzzle" | "comment_reply" | "comment_like" | "new_follower";
    read: boolean;
    time: string;
    // Navigation metadata
    actorNickname?: string; // For new_follower navigation to /profile/{nickname}
    entityId?: string; // For comment_reply navigation (comment ID)
    playableProperty?: {
        id?: string;
        shortLink?: string; // For puzzle navigation
        longLink?: string; // For comment_reply social listing
        property?: {
            id?: string;
            streetAddress?: string;
            city?: string;
            state?: string;
            zipCode?: string;
        };
    };
    playablePropertyComment?: {
        id?: string;
        body?: string;
    };
    // agent_puzzle fields
    agentName?: string;
    agentAvatar?: string;
    propertyAddress?: string;
    // comment_reply fields
    replierName?: string;
    replierAvatar?: string;
    commentSnippet?: string;
    puzzleTitle?: string;
    // comment_like fields
    likerName?: string;
    likerAvatar?: string;
    // new_follower fields
    followerName?: string;
    followerAvatar?: string;
}

function mixpanelNotificationType(n: AppNotification): MixpanelNotificationType {
    if (n.type === "agent_puzzle") return "new_puzzle_followed";
    if (n.type === "new_follower") return "new_follower";
    if (n.type === "comment_like") return "comment_like";
    return "comment_reply";
}

function notificationActorNickname(n: AppNotification): string | null {
    if (n.type === "agent_puzzle") return n.agentName ?? null;
    if (n.type === "new_follower") return n.followerName ?? null;
    if (n.type === "comment_like") return n.likerName ?? null;
    return n.replierName ?? null;
}
const getActorDisplayName = (actor?: InAppNotification["actor"]): string => {
    if (!actor) {
        return "Someone";
    }

    return String(actor.displayName || actor.nickname || "Someone");
};

const getPropertyAddress = (notification: InAppNotification): string | undefined => {
    const playableProperty = notification.playableProperty as
        | {
              property?: {
                  streetAddress?: string | null;
                  city?: string | null;
                  state?: string | null;
              } | null;
          }
        | undefined;

    if (!playableProperty) {
        return undefined;
    }

    const addressParts = [
        playableProperty.property?.streetAddress,
        playableProperty.property?.city,
        playableProperty.property?.state,
    ].filter(Boolean);

    if (!addressParts.length) {
        return undefined;
    }

    return addressParts.join(", ");
};

const mapInAppNotificationToAppNotification = (notification: InAppNotification): AppNotification => {
    const actorName = getActorDisplayName(notification.actor);
    const actorAvatar = notification.actor?.profilePhotoUrl;
    const actorNickname = notification.actor?.nickname;
    const time = notification.createdAt ? moment(notification.createdAt).fromNow() : "Recently";

    // Extract playable property data for navigation
    const playableProperty = notification.playableProperty as
        | {
              id?: string;
              shortLink?: string | null;
              longLink?: string | null;
              property?: {
                  id?: string | null;
                  streetAddress?: string | null;
                  city?: string | null;
                  state?: string | null;
                  zipCode?: string | null;
              } | null;
          }
        | undefined;
    const playablePropertyComment = notification.playablePropertyComment as
        | {
              id?: string;
              body?: string | null;
          }
        | undefined;

    const baseNotification = {
        id: notification.id,
        read: notification.read,
        time,
        actorNickname: actorNickname || undefined,
        entityId: notification.entityId || undefined,
        playableProperty: playableProperty
            ? {
                  id: playableProperty.id || undefined,
                  shortLink: playableProperty.shortLink || undefined,
                  longLink: playableProperty.longLink || undefined,
                  property: playableProperty.property
                      ? {
                            id: playableProperty.property.id || undefined,
                            streetAddress: playableProperty.property.streetAddress || undefined,
                            city: playableProperty.property.city || undefined,
                            state: playableProperty.property.state || undefined,
                            zipCode: playableProperty.property.zipCode || undefined,
                        }
                      : undefined,
              }
            : undefined,
        playablePropertyComment: playablePropertyComment
            ? {
                  id: playablePropertyComment.id || undefined,
                  body: playablePropertyComment.body || undefined,
              }
            : undefined,
    };

    switch (notification.type) {
        case "new_puzzle_followed":
            return {
                ...baseNotification,
                type: "agent_puzzle",
                agentName: actorName,
                agentAvatar: actorAvatar,
                propertyAddress: getPropertyAddress(notification),
            };
        case "new_follower":
            return {
                ...baseNotification,
                type: "new_follower",
                followerName: actorName,
                followerAvatar: actorAvatar,
            };
        case "comment_like":
            return {
                ...baseNotification,
                type: "comment_like",
                likerName: actorName,
                likerAvatar: actorAvatar,
                propertyAddress: getPropertyAddress(notification),
            };
        case "comment_reply":
        default:
            return {
                ...baseNotification,
                type: "comment_reply",
                replierName: actorName,
                replierAvatar: actorAvatar,
                commentSnippet: playablePropertyComment?.body || undefined,
                puzzleTitle: getPropertyAddress(notification),
            };
    }
};

const COLORS = {
    bg: "#1a1a2e",
    cardBg: "#232340",
    purple: "#7c3aed",
    purpleLight: "#a78bfa",
    orange: "#f97316",
    text: "#ffffff",
    textMuted: "#9ca3af",
    textDim: "#6b7280",
    border: "#3a3a5c",
    unreadBg: "rgba(124,58,237,0.08)",
};

function AvatarCircle({ src, name, size = 40 }: { src?: string; name: string; size?: number }) {
    const [hasImageError, setHasImageError] = useState(false);
    const colors = [
        "tw-bg-violet-600",
        "tw-bg-orange-500",
        "tw-bg-green-500",
        "tw-bg-blue-500",
        "tw-bg-red-500",
        "tw-bg-yellow-500",
    ];
    const idx = name.charCodeAt(0) % colors.length;
    const sizeClass = size <= 32 ? "tw-w-8 tw-h-8 tw-text-xs" : "tw-w-10 tw-h-10 tw-text-sm";

    useEffect(() => {
        setHasImageError(false);
    }, [src]);

    if (src && !hasImageError) {
        return (
            <div className="tw-relative tw-flex-shrink-0">
                <img
                    src={src}
                    alt={`${name} profile`}
                    className={`${sizeClass} tw-rounded-full tw-object-cover`}
                    onError={() => setHasImageError(true)}
                />
                <span className="tw-sr-only">{name} profile</span>
            </div>
        );
    }

    return (
        <div
            className={`tw-flex tw-items-center tw-justify-center tw-text-white tw-rounded-full tw-font-bold tw-flex-shrink-0 ${sizeClass} ${colors[idx]}`}
        >
            {name[0].toUpperCase()}
        </div>
    );
}

function NotificationItem({
    notification,
    onMarkRead,
    onNavigate,
    onClickTrack,
}: {
    notification: AppNotification;
    onMarkRead: (id: string) => Promise<void>;
    onNavigate: (notification: AppNotification) => void;
    onClickTrack: (notification: AppNotification) => void;
}) {
    const { type, read, time } = notification;
    const [isMarkingRead, setIsMarkingRead] = useState(false);

    const handleClick = async () => {
        onClickTrack(notification);

        if (isMarkingRead) return;

        try {
            // Only call mark as read API if notification is unread
            if (!read) {
                setIsMarkingRead(true);
                await onMarkRead(notification.id);
            }
            // Always navigate regardless of read status
            onNavigate(notification);
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
            // Still navigate even if marking as read fails
            onNavigate(notification);
        } finally {
            setIsMarkingRead(false);
        }
    };

    return (
        <button
            onClick={handleClick}
            disabled={isMarkingRead}
            className="tw-w-full tw-flex tw-items-start tw-text-left tw-cursor-pointer tw-gap-3 tw-p-4 tw-border-b tw-whitespace-normal disabled:tw-opacity-70 disabled:tw-cursor-not-allowed"
            style={{
                borderBottomColor: COLORS.border,
                background: read ? "transparent" : COLORS.unreadBg,
            }}
        >
            {/* Icon / Avatar */}
            <div className="tw-relative tw-flex-shrink-0">
                {type === "agent_puzzle" ? (
                    <AvatarCircle src={notification.agentAvatar} name={notification.agentName || "A"} size={40} />
                ) : type === "new_follower" ? (
                    <AvatarCircle src={notification.followerAvatar} name={notification.followerName || "U"} size={40} />
                ) : type === "comment_like" ? (
                    <AvatarCircle src={notification.likerAvatar} name={notification.likerName || "U"} size={40} />
                ) : (
                    <AvatarCircle name={notification.replierName || "U"} size={40} />
                )}
                {/* Type icon overlay */}
                <div
                    className="tw-flex tw-items-center tw-justify-center tw-absolute tw--bottom-0.5 tw--right-0.5 tw-w-[18px] tw-h-[18px] tw-rounded-full tw-border-2"
                    style={{
                        borderColor: COLORS.bg,
                        background:
                            type === "agent_puzzle"
                                ? COLORS.purple
                                : type === "new_follower"
                                  ? "#3b82f6"
                                  : type === "comment_like"
                                    ? COLORS.orange
                                    : COLORS.orange,
                    }}
                >
                    {type === "agent_puzzle" ? (
                        <Home size={9} color="white" />
                    ) : type === "new_follower" ? (
                        <UserPlus size={9} color="white" />
                    ) : type === "comment_like" ? (
                        <Heart size={9} color="white" fill="white" />
                    ) : (
                        <MessageCircle size={9} color="white" />
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="tw-flex-1 tw-min-w-0 tw-w-0">
                {type === "agent_puzzle" ? (
                    <p className="tw-block tw-w-full tw-text-xs tw-m-0 tw-leading-tight" style={{ color: COLORS.text }}>
                        <span className="tw-font-bold">{notification.agentName}</span> shared a new puzzle
                    </p>
                ) : type === "new_follower" ? (
                    <p className="tw-block tw-w-full tw-text-xs tw-m-0 tw-leading-tight" style={{ color: COLORS.text }}>
                        <span className="tw-font-bold">{notification.followerName}</span> started following you
                    </p>
                ) : type === "comment_like" ? (
                    <p className="tw-block tw-w-full tw-text-xs tw-m-0 tw-leading-tight" style={{ color: COLORS.text }}>
                        <span className="tw-font-bold">{notification.likerName}</span> liked your comment on{" "}
                        {notification.propertyAddress ?? "a property"}
                    </p>
                ) : (
                    <p className="tw-block tw-w-full tw-text-xs tw-m-0 tw-leading-tight" style={{ color: COLORS.text }}>
                        <span className="tw-font-bold">{notification.replierName}</span> replied to your comment
                    </p>
                )}

                {/* Secondary line */}
                {type !== "new_follower" && type !== "comment_like" && (
                    <p
                        className="tw-block tw-w-full tw-text-xs tw-mt-1 tw-mb-0 tw-leading-tight tw-whitespace-normal"
                        style={{ color: COLORS.textMuted, overflowWrap: "anywhere", wordBreak: "break-word" }}
                    >
                        {type === "agent_puzzle"
                            ? (notification.playableProperty?.property?.streetAddress ?? "Property update")
                            : `"${notification.playablePropertyComment?.body ?? "New reply"}"`}
                    </p>
                )}

                {/* Tertiary line for comment reply */}
                {type === "comment_reply" && notification.playableProperty?.property?.streetAddress && (
                    <p
                        className="tw-block tw-w-full tw-text-xs tw-mt-0.5 tw-mb-0 tw-leading-tight tw-whitespace-normal"
                        style={{ color: COLORS.textDim, overflowWrap: "anywhere", wordBreak: "break-word" }}
                    >
                        on {notification.playableProperty?.property?.streetAddress}
                    </p>
                )}
            </div>

            {/* Time + unread dot */}
            <div className="tw-flex tw-flex-col tw-items-end tw-gap-1.5 tw-flex-shrink-0">
                <span className="tw-text-xs tw-whitespace-nowrap" style={{ color: COLORS.textDim }}>
                    {time}
                </span>
                {!read && <div className="tw-w-2 tw-h-2 tw-rounded-full" style={{ background: COLORS.orange }} />}
            </div>
        </button>
    );
}

interface BaseNotificationsPanelProps {
    open: boolean;
    onClose: () => void;
}
interface ControlledNotificationsPanelProps extends BaseNotificationsPanelProps {
    notifications: AppNotification[];
    onNotificationsChange: (nextNotifications: AppNotification[]) => void;
}
interface UncontrolledNotificationsPanelProps extends BaseNotificationsPanelProps {
    notifications?: undefined;
    onNotificationsChange?: undefined;
}
type NotificationsPanelProps = ControlledNotificationsPanelProps | UncontrolledNotificationsPanelProps;
export function NotificationsPanel({
    open,
    onClose,
    notifications: notificationsProp,
    onNotificationsChange,
}: NotificationsPanelProps) {
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const userStateRef = useRef(userState);
    userStateRef.current = userState;
    const wasDrawerOpenRef = useRef(false);
    const [internalNotifications, setInternalNotifications] = useState<AppNotification[]>([]);
    const [isVisible, setIsVisible] = useState<boolean>(open);
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const [isOpening, setIsOpening] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const ANIMATION_DURATION = 260;
    const NOTIFICATIONS_PAGE_SIZE = 10;
    const isControlled = notificationsProp !== undefined && onNotificationsChange !== undefined;
    const notifications: AppNotification[] = isControlled ? notificationsProp : internalNotifications;
    const { isAuthenticated } = useAuth0();
    const queryClient = useQueryClient();

    const navigate = useNavigate();

    const {
        data: inAppNotificationsResponse,
        isLoading: isLoadingNotifications,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ["in-app-notifications", userState?.id, currentPage, NOTIFICATIONS_PAGE_SIZE],
        queryFn: () =>
            getInAppNotifications(String(userState?.id), {
                page: currentPage,
                limit: NOTIFICATIONS_PAGE_SIZE,
            }),
        enabled: Boolean(open && isAuthenticated && userState?.id),
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    });

    const unreadCount = notifications.filter((n) => !n.read).length;
    const hasNextPage = Boolean(inAppNotificationsResponse?.hasNextPage);

    const updateNotifications = (updater: (prevNotifications: AppNotification[]) => AppNotification[]) => {
        if (isControlled) {
            const nextNotifications = updater(notifications);
            onNotificationsChange(nextNotifications);
            return;
        }
        setInternalNotifications((prevNotifications) => updater(prevNotifications));
    };

    const handleNotificationClickTrack = (notification: AppNotification) => {
        try {
            const actorNickname = notificationActorNickname(notification);
            sendMixpanelEvent("Notification Click", {
                pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
                email: userState?.emailAddress ?? null,
                nickname: userState?.nickname ?? null,
                notificationActorNickname: actorNickname,
                notificationType: mixpanelNotificationType(notification),
            });
        } catch (error) {
            logger.warn("Failed to send Notification Click mixpanel event", {
                context: "NotificationsPanel.handleNotificationClickTrack",
                error: error instanceof Error ? error.message : String(error),
            });
        }
    };

    const handleNotificationNavigation = (notification: AppNotification) => {
        try {
            if (notification.type === "new_follower") {
                // Navigate to user profile using nickname
                const nickname = notification.actorNickname;
                if (nickname) {
                    navigate(`/profile/${encodeURIComponent(nickname)}`);
                } else {
                    console.warn("Missing actorNickname for new_follower navigation");
                }
            } else if (notification.type === "comment_reply" || notification.type === "comment_like") {
                const commentId = notification.entityId;
                const didNavigate = navigateToPuzzleScoreboardFromNotification({
                    navigate,
                    shortLink: notification.playableProperty?.shortLink,
                    longLink: notification.playableProperty?.longLink,
                    focusCommentId: commentId,
                });
                if (!didNavigate) {
                    console.warn("Missing playable property link for comment notification navigation", {
                        hasShortLink: Boolean(notification.playableProperty?.shortLink),
                        hasLongLink: Boolean(notification.playableProperty?.longLink),
                        entityId: commentId,
                    });
                }
            } else if (notification.type === "agent_puzzle") {
                const didNavigate = navigateToPuzzleScoreboardFromNotification({
                    navigate,
                    shortLink: notification.playableProperty?.shortLink,
                    longLink: notification.playableProperty?.longLink,
                });
                if (!didNavigate) {
                    console.warn("Missing playable property link for agent_puzzle navigation");
                }
            }
            onClose();
        } catch (error) {
            console.error("Navigation error:", error);
        }
    };

    const handleMarkRead = async (id: string): Promise<void> => {
        if (!userState?.id) {
            console.warn("Cannot mark notification as read: user not authenticated");
            return;
        }

        try {
            await markInAppNotificationAsRead(String(userState.id), id);
            updateNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
            await queryClient.invalidateQueries({
                queryKey: ["in-app-unread-notification-count", userState.id],
            });
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
            logger.error("Failed to mark notification as read", error, {
                context: "NotificationsPanel.handleMarkRead",
                userId: userState.id,
                notificationId: id,
            });
            throw error;
        }
    };

    const handleMarkAllRead = async () => {
        if (!userState?.id) {
            console.warn("Cannot mark notifications as read: user not authenticated");
            return;
        }

        try {
            await markAllInAppNotificationsAsRead(String(userState.id));
            updateNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ["in-app-notifications", userState.id],
                }),
                queryClient.invalidateQueries({
                    queryKey: ["in-app-unread-notification-count", userState.id],
                }),
            ]);
        } catch (error) {
            console.error("Failed to mark all notifications as read:", error);
            logger.error("Failed to mark all notifications as read", error, {
                context: "NotificationsPanel.handleMarkAllRead",
                userId: userState.id,
            });
        }
    };

    useEffect(() => {
        if (open) {
            setCurrentPage(1);
            // Only clear notifications on successful load or initial load, not on error
            if (isAuthenticated && userState?.id && !isError) {
                if (isControlled) {
                    onNotificationsChange([]);
                } else {
                    setInternalNotifications([]);
                }
            }
            if (isAuthenticated && userState?.id) {
                void queryClient.invalidateQueries({
                    queryKey: ["in-app-notifications", userState.id],
                });
            }
            setIsVisible(true);
            setIsClosing(false);
            setIsOpening(true);
            const raf = requestAnimationFrame(() => setIsOpening(false));
            return () => cancelAnimationFrame(raf);
        }

        if (!isVisible) {
            return;
        }

        setIsClosing(true);
        const timer = setTimeout(() => {
            setIsVisible(false);
            setIsClosing(false);
        }, ANIMATION_DURATION);

        return () => clearTimeout(timer);
    }, [isAuthenticated, isControlled, isVisible, onNotificationsChange, open, userState?.id, isError, queryClient]);

    useEffect(() => {
        if (!inAppNotificationsResponse) {
            return;
        }

        const nextNotifications = inAppNotificationsResponse.data.map(mapInAppNotificationToAppNotification);

        const areNotificationsEqual = (left: AppNotification[], right: AppNotification[]) => {
            if (left.length !== right.length) {
                return false;
            }
            return left.every((item, index) => {
                const other = right[index];
                return (
                    item.id === other.id &&
                    item.type === other.type &&
                    item.read === other.read &&
                    item.time === other.time &&
                    item.agentName === other.agentName &&
                    item.agentAvatar === other.agentAvatar &&
                    item.propertyAddress === other.propertyAddress &&
                    item.replierName === other.replierName &&
                    item.replierAvatar === other.replierAvatar &&
                    item.commentSnippet === other.commentSnippet &&
                    item.puzzleTitle === other.puzzleTitle &&
                    item.followerName === other.followerName &&
                    item.followerAvatar === other.followerAvatar &&
                    // Navigation metadata fields
                    item.actorNickname === other.actorNickname &&
                    item.entityId === other.entityId &&
                    item.playableProperty?.shortLink === other.playableProperty?.shortLink &&
                    item.playableProperty?.longLink === other.playableProperty?.longLink &&
                    item.playableProperty?.property?.id === other.playableProperty?.property?.id &&
                    item.playableProperty?.property?.streetAddress ===
                        other.playableProperty?.property?.streetAddress &&
                    item.playableProperty?.property?.city === other.playableProperty?.property?.city &&
                    item.playableProperty?.property?.state === other.playableProperty?.property?.state &&
                    item.playableProperty?.property?.zipCode === other.playableProperty?.property?.zipCode
                );
            });
        };

        if (isControlled) {
            const currentNotifications = notificationsProp || [];
            const mergedNotifications =
                currentPage === 1
                    ? nextNotifications
                    : [
                          ...currentNotifications,
                          ...nextNotifications.filter(
                              (next) => !currentNotifications.some((item) => item.id === next.id),
                          ),
                      ];

            if (!areNotificationsEqual(currentNotifications, mergedNotifications)) {
                onNotificationsChange!(mergedNotifications);
            }
            return;
        }

        setInternalNotifications((prevNotifications) => {
            const mergedNotifications =
                currentPage === 1
                    ? nextNotifications
                    : [
                          ...prevNotifications,
                          ...nextNotifications.filter((next) => !prevNotifications.some((item) => item.id === next.id)),
                      ];

            if (areNotificationsEqual(prevNotifications, mergedNotifications)) {
                return prevNotifications;
            }
            return mergedNotifications;
        });
    }, [currentPage, inAppNotificationsResponse, isControlled, notificationsProp, onNotificationsChange]);

    useEffect(() => {
        const opening = open && !wasDrawerOpenRef.current;
        wasDrawerOpenRef.current = open;
        if (!opening) return;
        const u = userStateRef.current;
        sendMixpanelEvent("Notifications View", {
            email: u?.emailAddress ?? null,
            nickname: u?.nickname ?? null,
        });
    }, [open, sendMixpanelEvent]);

    if (!isVisible) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                className="tw-fixed tw-inset-0 tw-bg-black/50"
                style={{
                    zIndex: 120,
                    opacity: isClosing || isOpening ? 0 : 1,
                    transition: `opacity ${ANIMATION_DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                }}
            />

            {/* Panel */}
            <div
                className="tw-fixed tw-right-0 tw-top-0 tw-bottom-0 tw-flex tw-flex-col tw-w-[340px] tw-shadow-[-4px_0_20px_rgba(0,0,0,0.4)] tw-font-sans"
                style={{
                    background: COLORS.bg,
                    zIndex: 130,
                    transform: isClosing || isOpening ? "translateX(100%)" : "translateX(0)",
                    opacity: isClosing || isOpening ? 0.96 : 1,
                    transition: `transform ${ANIMATION_DURATION}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${ANIMATION_DURATION}ms ease`,
                }}
            >
                {/* Panel Header */}
                <div
                    className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-pt-5 tw-pb-3.5 tw-border-b"
                    style={{ borderBottomColor: COLORS.border }}
                >
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <h2 className="tw-text-lg tw-font-bold tw-m-0 tw-font-serif" style={{ color: COLORS.text }}>
                            Notifications
                        </h2>
                        {unreadCount > 0 && (
                            <div
                                className="tw-flex tw-items-center tw-justify-center tw-text-white tw-min-w-5 tw-h-5 tw-rounded-full tw-text-xs tw-font-bold tw-px-1.5"
                                style={{ background: COLORS.orange }}
                            >
                                {unreadCount}
                            </div>
                        )}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="tw-bg-none tw-border-none tw-text-xs tw-font-medium tw-cursor-pointer tw-p-0"
                                style={{ color: COLORS.purpleLight }}
                            >
                                Mark all read
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="tw-bg-none tw-border-none tw-cursor-pointer tw-p-1 tw-flex"
                        >
                            <X size={20} color={COLORS.textMuted} />
                        </button>
                    </div>
                </div>

                {/* Notification List */}
                <div className="tw-flex-1 tw-overflow-y-auto">
                    {isError ? (
                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-[60px] tw-px-5 tw-gap-4">
                            <span className="tw-text-4xl">⚠️</span>
                            <div className="tw-text-center">
                                <p className="tw-text-sm tw-font-medium tw-mb-2" style={{ color: COLORS.text }}>
                                    Failed to load notifications
                                </p>
                                <p className="tw-text-xs tw-mb-4" style={{ color: COLORS.textMuted }}>
                                    {error instanceof Error ? error.message : "Something went wrong. Please try again."}
                                </p>
                                <button
                                    onClick={async () => {
                                        try {
                                            // Reset to first page and refetch
                                            setCurrentPage(1);
                                            // Invalidate and refetch the notifications query
                                            await queryClient.invalidateQueries({
                                                queryKey: ["in-app-notifications", userState?.id],
                                            });
                                            // Also try direct refetch as fallback
                                            await refetch();
                                        } catch (retryError) {
                                            console.error("Failed to retry notifications fetch:", retryError);
                                            logger.error("Failed to retry notifications fetch", retryError, {
                                                context: "NotificationsPanel.retryButton",
                                                userId: userState?.id,
                                            });
                                        }
                                    }}
                                    className="tw-bg-none tw-border tw-rounded-full tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-cursor-pointer hover:tw-opacity-80"
                                    style={{ color: COLORS.purpleLight, borderColor: COLORS.border }}
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    ) : isLoadingNotifications && notifications.length === 0 ? (
                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-[60px] tw-px-5 tw-gap-2">
                            <p className="tw-text-sm tw-text-center" style={{ color: COLORS.textMuted }}>
                                Loading notifications...
                            </p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-[60px] tw-px-5 tw-gap-2">
                            <span className="tw-text-4xl">🔔</span>
                            <p className="tw-text-sm tw-text-center" style={{ color: COLORS.textMuted }}>
                                No notifications yet
                            </p>
                        </div>
                    ) : (
                        <>
                            {notifications.map((n) => (
                                <NotificationItem
                                    key={n.id}
                                    notification={n}
                                    onMarkRead={handleMarkRead}
                                    onNavigate={handleNotificationNavigation}
                                    onClickTrack={handleNotificationClickTrack}
                                />
                            ))}
                            {hasNextPage && (
                                <div className="tw-flex tw-justify-center tw-px-4 tw-py-4">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage((prev) => prev + 1)}
                                        disabled={isLoadingNotifications}
                                        className="tw-bg-none tw-border tw-rounded-full tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-cursor-pointer disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                                        style={{ color: COLORS.purpleLight, borderColor: COLORS.border }}
                                    >
                                        {isLoadingNotifications ? "Loading..." : "Load more"}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
